import * as React from 'react';
import { HandsHoldingPlantIcon, MedalIcon, DesktopIcon, ConversationIcon } from './icons';
import style from './style.module.scss';
import cx from 'classnames';
import * as moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import RecommendationsForm from './tutor_recommendations_form';
import { paths } from 'config/path_helpers';
import { DefaultButton } from '@magoosh/lib/button';
import { RecommendationRequest } from './types';

interface Props extends RecommendationRequest {
  mockTestId: number;
  isSubmitted: boolean;
  onComplete: () => void;
}

const RecommendationsTabCompleted: React.FC<Props> = (props) => {

  const renderEmptyMessage = () => {
    return (
      <div className={cx(style.empty, 'u-padding-A-l')}>
        <p>Nothing to display yet.</p>
      </div>
    );
  };

  const renderTutorFeedback = () => {
    return (
      <div>
        <p className={style.tutorFeedbackTitle}>
          <ConversationIcon fill={style.brandPrimary} />
          <span className="u-margin-L-xs">{`${props.tutor.name} on ${moment(props.updatedAt * 1000).format('MMMM DD, YYYY')}`}</span>
        </p>
        <p className={style.freeformRecommendation}>{props.recommendations.freeform}</p>
      </div>
    );
  };

  const renderRecommendedModules = () => {
    return (
      <div>
        {props.recommendations.modules.map((module) => (
          <div key={module.id} className={cx(style.recommendedModule, 'u-padding-V-m')}>
            <div>
              <h4><strong>{module.name}</strong></h4>
              <p>{module.estimatedTimeMinutes} minutes</p>
            </div>
            <div>
              <DefaultButton href={paths.lessons()}>
                View on Lessons Page
              </DefaultButton>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={cx(style.tutorRecommedationTab, style.tutorRecommedationTabSpacing)}>
      {!props.isComplete && (
        <div className={cx(style.banner, 'u-padding-A-l')}>
          <strong>Your recommendations are on the way!</strong> <br />
          You’ll receive an email as soon as they are ready–typically within 1-2 business days.
        </div>
      )}

      <div className="u-margin-T-m u-margin-B-xl">
        <div className={cx(style.card, 'u-padding-V-l', 'u-padding-H-m', 'u-margin-V-l')}>
          <Row className={style.row}>
            <Col xs={12} sm={4}>
              <MedalIcon fill={style.brandPrimary} height={80} width={80} />
              <h3>
                <strong>Tutor Feedback</strong>
              </h3>
              <p>These comments are based on your goals and practice test results.</p>
            </Col>
            <Col xs={12} sm={8}>
              {props.isComplete ? renderTutorFeedback() : renderEmptyMessage()}
            </Col>
          </Row>
        </div>
        <div className={cx(style.card, 'u-padding-V-l', 'u-padding-H-m', 'u-margin-V-l')}>
          <Row className={style.row}>
            <Col xs={12} sm={4}>
              <DesktopIcon fill={style.moderateSignificance} height={80} width={80} />
              <h3>
                <strong>Tutor Recommended Modules</strong>
              </h3>
              <p>
                For easy access we’ve added these to the Lessons page.
              </p>
              <a style={{ color: style.brandSecondary }} href={paths.lessons()}>
                <strong>Go to Lessons</strong>
              </a>
            </Col>
            <Col xs={12} sm={8}>
              {props.isComplete ? renderRecommendedModules() : renderEmptyMessage()}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

const RecommendationsTabIncompleted: React.FC<Props & { onComplete: () => void }> = (props) => (
  <>
    <Row className={cx(style.tutorRecommedationTab, 'u-margin-A-n')}>
      <Col xs={12} md={5}>
        <div className="u-padding-A-xl">
          <HandsHoldingPlantIcon fill={style.brandPrimary} />
          <h3>
            <strong>Get Study Recommendations from a Tutor</strong>
          </h3>
          <p className="text-gray">
            One of our GRE tutors will review your practice test results and provide you with personalized
            advice within 1-2 business days.
          </p>
          <p className="text-gray">
            To get started, please tell us about your goals and how you feel about your performance on this
            practice test. All questions are required.
          </p>
        </div>
      </Col>
      <Col className={cx(style.formContainer, 'u-padding-A-l')} xs={12} md={7}>
        <RecommendationsForm onComplete={props.onComplete} mockTestId={props.mockTestId} />
      </Col>
    </Row>
  </>
);

export const TutorRecommendationsTabGre: React.FC<Props> = (props) => {
  return !props.isSubmitted ? (
    <RecommendationsTabIncompleted {...props} onComplete={props.onComplete} />
  ) : (
    <RecommendationsTabCompleted {...props} />
  );
};

export default TutorRecommendationsTabGre;
